import { createSelector } from '@reduxjs/toolkit';
import cloneDeep from 'lodash-es/cloneDeep';

import { AnalyticsType } from '@/shared/types/analytics';
import { RootState } from '@/shared/types/state';

const selectAnalytics = (state: RootState) => state.analytics.accountAnalytics;

const selectAnalyticsStartDate = (state: RootState) => state.analytics.startDate;
const selectAnalyticsEndDate = (state: RootState) => state.analytics.endDate;

const selectFiltersLoaded = (state: RootState) => state.analytics.filtersLoaded;
const selectLogs = (state: RootState) => state.analytics.logs;
export const selectLogsFiltersPanel = (state: RootState) => state.analytics.logsFiltersPanel;

export const selectAnalyticsFiltersLoaded = (state: RootState) => state.analytics.filtersLoaded;

export const selectFilters = createSelector(
  [
    selectAnalytics,
    selectAnalyticsStartDate,
    selectAnalyticsEndDate,
    selectFiltersLoaded,
    selectLogs,
  ],
  (analytics, startDate, endDate, filtersLoaded, logs) => ({
    analytics,
    startDate,
    endDate,
    filtersLoaded,
    logs,
  })
);

const selectFilterPage = (state: RootState, filterPage: string) => state[filterPage];

export const selectStartDate = createSelector(
  [selectFilterPage],
  (filterPage) => filterPage.startDate
);

export const selectEndDate = createSelector([selectFilterPage], (filterPage) => filterPage.endDate);

export const makeSelectFiltersByPageType =
  (filterPage: 'analytics', type: AnalyticsType) => (state: RootState) =>
    state[filterPage][type];

export const selectAnalyticsDates = createSelector(
  [selectAnalyticsStartDate, selectAnalyticsEndDate],
  (startDate, endDate) => ({ startDate, endDate })
);

export const makeSelectStartDate = (filterPage: 'analytics') => (state: RootState) =>
  selectStartDate(state, filterPage);

export const makeSelectEndDate = (filterPage: 'analytics') => (state: RootState) =>
  selectEndDate(state, filterPage);

export const selectFiltersByType = createSelector(
  (state: RootState, type: AnalyticsType) => state.analytics[type],
  (analyticsType) => {
    const newFilters = cloneDeep(analyticsType) as { type: 'channels'; channels: string[] }[];

    if (!newFilters || newFilters.length === 0) {
      return [];
    }

    const channels = newFilters?.find((log) => log.type === 'channels');

    if (channels) {
      const newChannels = { channels: channels.channels, type: 'channels' as const };

      const channelsIndex = newFilters.findIndex((log) => log.type === 'channels');
      newFilters.splice(channelsIndex, 1, newChannels);
    }
    return newFilters;
  }
);
